/**
 * Customise the offering: pick the option, write the dedication.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import { CardHeading, EmptyState, Notice, StepsTrail } from '../components/ui';
import { useBooking } from '../context/BookingContext';
import { api } from '../lib/api';
import { formatAmount, containsTibetan } from '../lib/format';
import FeeSplit from '../components/FeeSplit';
import { trackEvent } from '../lib/consent';

export default function Offer() {
  const { t } = useTranslation();
  const { id, serviceId } = useParams();
  const navigate = useNavigate();
  const { draft, updateDraft } = useBooking();

  const [monastery, setMonastery] = useState(null);
  const [error, setError] = useState('');
  const [option, setOption] = useState(null);
  const [dedication, setDedication] = useState(draft.dedicationText || '');
  const [tibetan, setTibetan] = useState(draft.tibetanDedicationText || '');
  const [donorName, setDonorName] = useState(draft.donorName || '');
  const [share, setShare] = useState(draft.shareDedication || false);

  const defaultDedication = t(
    'offer.inEnglishPlaceholder',
    'May all sentient beings be free from suffering and possess the causes of happiness.'
  );

  useEffect(() => {
    const controller = new AbortController();
    // The monastery record already carries every service and its options
    // (`repositories/partners.js#activeServices`, server-side) — no separate
    // per-service fetch needed.
    api
      .monastery(id, controller.signal)
      .then(({ monastery: record }) => {
        setMonastery(record);
        const service = (record.services ?? []).find((s) => s.id === serviceId);
        const options = service?.options ?? [];
        const previous = options.find((o) => o.id === draft.option?.id);
        setOption(previous ?? options[0] ?? null);
      })
      .catch((err) => err.name !== 'AbortError' && setError(err.message));
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, serviceId]);

  const service = monastery?.services?.find((s) => s.id === serviceId);
  // "Flat" here means every option on this service shares one price rather
  // than scaling with quantity — the per-option pricing this reads from
  // (`option.amountCents`, set independently per option) is what actually
  // fixed the old flat-pricing bug ("3-Day Retreat" costing the same as
  // "Single Day"): each option now carries its own real amount, not a
  // shared `service.price` guessed at by the option's JS type.
  const isFlatPriced = service?.options?.every((o) => !o.isPerUnit);

  const submit = (event) => {
    event.preventDefault();
    updateDraft({
      monastery,
      service,
      option,
      dedicationText: dedication,
      tibetanDedicationText: tibetan,
      donorName,
      shareDedication: share,
    });
    trackEvent('begin_checkout', {
      value: (option?.amountCents ?? 0) / 100,
      currency: 'USD',
      item_id: service.id,
      option: option?.id,
    });
    navigate('/checkout');
  };

  if (error) {
    return (
      <div className="shell section">
        <EmptyState
          icon="alert"
          title={t('offer.offeringUnavailable', 'That offering is not available')}
          action={<Link to="/explore" className="btn btn--primary">{t('offer.backToExplore', 'Back to Explore')}</Link>}
        >
          {error}
        </EmptyState>
      </div>
    );
  }

  if (monastery && !service) {
    return (
      <div className="shell section">
        <EmptyState
          icon="alert"
          title={t('offer.offeringUnavailable', 'That offering is not available')}
          action={<Link to="/explore" className="btn btn--primary">{t('offer.backToExplore', 'Back to Explore')}</Link>}
        />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="shell section">
        <div className="skeleton" style={{ height: 260, borderRadius: 'var(--radius-lg)' }} />
      </div>
    );
  }

  return (
    <div className="shell has-cta-bar" style={{ paddingTop: 'var(--space-3xl)' }}>
      <div className="stack" style={{ marginBottom: 'var(--space-2xl)' }}>
        <Link to={`/monastery/${monastery.slug || monastery.id}`} className="btn btn--quiet" style={{ justifySelf: 'start', paddingInline: 0 }}>
          <Icon name="arrowLeft" size={16} /> {monastery.name}
        </Link>
        <StepsTrail current={0} />
      </div>

      <form className="flow-layout" onSubmit={submit}>
        <div className="stack stack--lg">
          <header className="stack stack--sm">
            <p className="eyebrow">{service.category}</p>
            <h1 className="display display--md">{service.name}</h1>
            <p className="lede">{service.description}</p>
          </header>

          {/* ------------------------------------------------------- option -- */}
          <section className="card card__pad">
            <CardHeading icon="flame" title={t('offer.chooseOffering', 'Choose the offering')} />
            <div className="stack stack--sm" role="radiogroup" aria-label={t('offer.chooseOffering', 'Offering option')}>
              {service.options.map((choice) => {
                const selected = choice.id === option?.id;
                return (
                  <button
                    type="button"
                    key={choice.id}
                    role="radio"
                    aria-checked={selected}
                    className={`choice${selected ? ' is-selected' : ''}`}
                    onClick={() => {
                      setOption(choice);
                      trackEvent('select_offering_option', {
                        item_id: service.id,
                        option: choice.id,
                        value: (choice.amountCents ?? 0) / 100,
                        currency: 'USD',
                      });
                    }}
                  >
                    <span className="choice__dot" aria-hidden="true" />
                    <span>
                      <span className="choice__label">{choice.label}</span>
                      {choice.isPerUnit && (
                        <span className="choice__sub" style={{ display: 'block' }}>
                          {formatAmount(choice.unitPriceCents)} {t('monasteryDetail.perLamp', 'per lamp')}
                        </span>
                      )}
                    </span>
                    <span className="choice__price">{formatAmount(choice.amountCents)}</span>
                  </button>
                );
              })}
            </div>

            {isFlatPriced && service.options.length > 1 && (
              <p className="subtle" style={{ marginTop: 'var(--space-lg)' }}>
                {t('offer.sharedAmountNotice', 'These options currently share one contribution amount. Choose the one that best matches your intention.')}
              </p>
            )}
          </section>

          {/* --------------------------------------------------- dedication -- */}
          <section className="card card__pad">
            <CardHeading icon="quill" title={t('offer.yourDedication', 'Your dedication')} />
            <p className="field__hint" style={{ marginBottom: 'var(--space-lg)' }}>
              {t('offer.dedicationHint', 'This is the part the monks read aloud. Leave it blank if you want the aspiration for all beings offered instead.')}
            </p>

            <div className="stack">
              <div className="field">
                <label className="field__label" htmlFor="dedication">
                  {t('offer.inEnglish', 'In English')}
                </label>
                <textarea
                  id="dedication"
                  className="textarea"
                  maxLength={600}
                  value={dedication}
                  onChange={(event) => setDedication(event.target.value)}
                  placeholder={defaultDedication}
                />
                <p className="field__hint">{dedication.length}/600</p>
              </div>

              <div className="field">
                <label className="field__label" htmlFor="tibetan">
                  <Icon name="language" size={14} style={{ verticalAlign: -2, marginRight: 5 }} />
                  {t('offer.inTibetan', 'In Tibetan script')}
                  <span className="subtle" style={{ fontWeight: 500 }}> · {t('common.optional', 'optional')}</span>
                </label>
                <textarea
                  id="tibetan"
                  lang="bo"
                  className="textarea textarea--tibetan"
                  maxLength={600}
                  value={tibetan}
                  onChange={(event) => setTibetan(event.target.value)}
                  placeholder="སེམས་ཅན་ཐམས་ཅད་བདེ་བ་དང་ལྡན་པར་གྱུར་ཅིག།"
                />
                {tibetan && !containsTibetan(tibetan) && (
                  <p className="field__hint">
                    {t('offer.tibetanHint', 'This field is for Tibetan script. What you have written will still be passed on exactly as typed.')}
                  </p>
                )}
              </div>

              <div className="field">
                <label className="field__label" htmlFor="donor">
                  {t('offer.donorNameLabel', 'Name to offer it under')}
                  <span className="subtle" style={{ fontWeight: 500 }}> · {t('common.optional', 'optional')}</span>
                </label>
                <input
                  id="donor"
                  className="input"
                  maxLength={80}
                  value={donorName}
                  onChange={(event) => setDonorName(event.target.value)}
                  placeholder={t('offer.donorNamePlaceholder', 'Devoted Donor')}
                />
                <p className="field__hint">
                  {t('offer.donorNameHint', 'Used when the dedication is read. Leave blank to remain anonymous.')}
                </p>
              </div>

              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={share}
                  onChange={(event) => setShare(event.target.checked)}
                />
                <span>
                  <b style={{ fontSize: 13.5 }}>{t('offer.sharePublicly', 'Share this dedication publicly')}</b>
                  <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    {t('offer.sharePubliclyHint', 'Only the prayer and the name above appear on the public wall — never your email, and never the amount.')}
                  </span>
                </span>
              </label>
            </div>
          </section>
        </div>

        {/* --------------------------------------------------------- summary -- */}
        <aside className="summary-card card card__pad">
          <CardHeading icon="receipt" title={t('offer.yourOffering', 'Your offering')} />

          <dl>
            <div className="summary-line">
              <dt>{t('offer.monastery', 'Monastery')}</dt>
              <dd>{monastery.name}</dd>
            </div>
            <div className="summary-line">
              <dt>{t('offer.ritual', 'Ritual')}</dt>
              <dd>{service.name}</dd>
            </div>
            <div className="summary-line">
              <dt>{t('offer.offering', 'Offering')}</dt>
              <dd>{option?.label ?? '—'}</dd>
            </div>
          </dl>

          <div className="summary-total">
            <span className="muted" style={{ fontSize: 13 }}>{t('offer.contribution', 'Contribution')}</span>
            <b>{formatAmount(option?.amountCents)}</b>
          </div>

          <FeeSplit
            offeringCents={option?.amountCents ?? 0}
            bps={monastery?.platformFeeBps}
            monasteryName={monastery?.name}
          />

          <button type="submit" className="btn btn--primary btn--block" style={{ marginTop: 'var(--space-lg)' }}>
            {t('offer.continueToReview', 'Continue to Review')}
            <Icon name="arrowRight" size={17} />
          </button>

          <div style={{ marginTop: 'var(--space-lg)' }}>
            <Notice tone="info" icon="lotus">
              {t('offer.disclaimerNotice', 'Your contribution is an offering — it invites the monastery’s resident sangha to perform the ritual as part of their own practice.')}
            </Notice>
          </div>
        </aside>

        <div className="cta-bar">
          <div>
            <div className="cta-bar__label">{t('offer.contribution', 'Contribution')}</div>
            <div className="cta-bar__total">{formatAmount(option?.amountCents)}</div>
          </div>
          <button type="submit" className="btn btn--primary">
            {t('offer.continueToReview', 'Continue')}
            <Icon name="arrowRight" size={17} />
          </button>
        </div>
      </form>
    </div>
  );
}
